import { useContext } from 'react';
import { useNavigate } from 'react-router';

import { GlobalContext } from 'contexts/GlobalContext';
import CartProductCard from 'features/shop/cart/CartProductCard';
import { Alert } from 'honeydew/Alert';
import Button from 'honeydew/Button';

/**
 * CartFrozenItems - Displays frozen items section in the shopping cart
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes for styling the main section
 * @param {Object} [props.ebtCard] - EBT card information object
 * @param {string} [props.ebtCard.last4] - Last 4 digits of the EBT card
 * @param {string} [props.itemsContainerClassName] - Additional CSS classes for the items container
 * @param {number} [props.frozenFee] - Fee amount for frozen items (displayed if provided)
 * @param {number} [props.frozenFeeAmountRemaining] - Amount remaining to waive the frozen fee
 * @param {number} [props.frozenFeeWaiveThreshold] - Threshold for waiving the frozen fee
 * @param {Array<Object>} props.frozenItems - Array of frozen product objects
 * @param {number} props.frozenItemsQty - Total quantity of frozen items
 * @param {Function} props.onProductClick - Callback function when a product is clicked
 * @param {boolean} props.plusMembershipEligibleOrder - Whether the order is eligible for plus membership pricing
 * @param {string} props.plusMembershipShortBranding - Short branding text for plus membership (e.g., 'M+', 'IF+')
 * @param {boolean} props.waiveFrozenFee - Whether the frozen fee is waived
 *
 * @returns {JSX.Element|null} Rendered component or null if no frozen items
 */
function CartFrozenItems({
    className,
    ebtCard,
    frozenFee,
    frozenFeeAmountRemaining,
    frozenFeeWaiveThreshold,
    frozenItems,
    frozenItemsQty,
    itemsContainerClassName,
    onProductClick,
    plusMembershipEligibleOrder,
    plusMembershipShortBranding,
    waiveFrozenFee,
}) {
    const navigate = useNavigate();
    const { setCartOpen } = useContext(GlobalContext);

    if (frozenItems.length === 0) {
        return null;
    }

    const navigateToFrozenAisle = () => {
        if (window.isNativeApp) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'Frozen' }));
        } else {
            setCartOpen(false);
            navigate('/shop/aisle/frozen/all');
        }
    };

    return (
        <section className={className} data-testid="cart-frozen-items-section">
            <h3 className="text-body-md mb-10 scroll-mt-10 font-grotesk-bold">Frozen ({frozenItemsQty})</h3>
            <div className={itemsContainerClassName}>
                {frozenFee && frozenFeeWaiveThreshold > 0 && (
                    <div className="mb-10">
                        {waiveFrozenFee ? (
                            <Alert variant="success">Your frozen fee is waived!</Alert>
                        ) : (
                            <Alert>
                                Add <span className="font-grotesk-bold">${frozenFeeAmountRemaining?.toFixed(2)} </span>
                                to waive frozen fee
                            </Alert>
                        )}
                        <p className="text-body-xs mt-10 text-grey">
                            ${frozenFee?.toFixed(2)} Frozen Packaging Fee applies; purchase at least $
                            {frozenFeeWaiveThreshold?.toFixed(2)} of frozen items to waive the fee. These items ship
                            with dry ice and special packaging to keep them frozen in transit.
                        </p>
                    </div>
                )}
                {frozenFee && !frozenFeeWaiveThreshold && (
                    <div className="mb-10">
                        <Alert variant="info-bell">
                            <span className="text-body-xs leading-snug">
                                <span className="block font-grotesk-bold leading-snug">
                                    ${frozenFee?.toFixed(2)} Frozen Packaging Fee applies.
                                </span>
                                <span className="mt-2 block leading-snug">
                                    These items ship with dry ice and special packaging to keep them frozen in transit.
                                </span>
                            </span>
                        </Alert>
                    </div>
                )}
                <div className="flex flex-col gap-30">
                    {frozenItems.map((product) => (
                        <CartProductCard
                            ebtCard={ebtCard}
                            key={product.id}
                            plusMembershipEligibleOrder={plusMembershipEligibleOrder}
                            plusMembershipShortBranding={plusMembershipShortBranding}
                            onClick={() => onProductClick({ productId: product.id })}
                            product={product}
                            usePlusMemberPrice
                        />
                    ))}
                </div>
                <Button customClass="mt-10" onClick={navigateToFrozenAisle} size="md" variant="secondary">
                    Shop Frozen
                </Button>
            </div>
        </section>
    );
}

export default CartFrozenItems;
